import { useTranslation } from "react-i18next";
import type { AnalysisSummary, AppAnalysisHealth } from "@repo/shared";

import {
  ANALYSIS_CATEGORY_COUNT,
  formatBytes,
  getGradeColors,
  isCategoryScored,
} from "@/lib/health-format";

import { HealthCategoriesGrid } from "./HealthCategoriesGrid";
import { HealthFindingsList } from "./HealthFindingsList";
import { KpiCell, KpiStrip } from "./kpi";

// The QVD compare screen takes its KPI pieces from here.
export { KpiCell, KpiStrip };

interface HealthOverviewTabProps {
  summary: AnalysisSummary;
  health: AppAnalysisHealth;
}

export function HealthOverviewTab({ summary, health }: HealthOverviewTabProps) {
  const { t } = useTranslation("analyses");

  const appSizeValue =
    summary.appFileSize > 0 ? formatBytes(summary.appFileSize) : formatBytes(summary.totalBytes);
  const appSizeSub =
    summary.staticByteSize > 0
      ? t("overview.kpi.appSizeSub", {
          memory: formatBytes(summary.staticByteSize),
        })
      : undefined;

  const fieldMemory = summary.dataBytes || summary.totalBytes;
  const wastedMemory = summary.wastedBytes + summary.wastedIndexBytes;
  const wastedPct = fieldMemory > 0 ? ((wastedMemory / fieldMemory) * 100).toFixed(1) : "0";
  const indirectWastedTotal =
    (summary.indirectWastedBytes ?? 0) + (summary.indirectWastedIndexBytes ?? 0);
  const orphanWastedTotal =
    (summary.orphanWastedBytes ?? 0) + (summary.orphanWastedIndexBytes ?? 0);
  const unusedOnlyWastedTotal = Math.max(0, wastedMemory - indirectWastedTotal - orphanWastedTotal);
  const wastedParts = [
    t("summary.wastedMemoryPartUnused", { value: formatBytes(unusedOnlyWastedTotal) }),
    ...(indirectWastedTotal > 0
      ? [t("summary.wastedMemoryPartIndirect", { value: formatBytes(indirectWastedTotal) })]
      : []),
    ...(orphanWastedTotal > 0
      ? [t("summary.wastedMemoryPartOrphan", { value: formatBytes(orphanWastedTotal) })]
      : []),
  ];
  const wastedMemorySub =
    indirectWastedTotal > 0 || orphanWastedTotal > 0
      ? t("summary.wastedMemoryDetailParts", { pct: wastedPct, parts: wastedParts.join(" · ") })
      : t("summary.wastedMemoryDetail", { pct: wastedPct });
  const evaluatedCategories = health.categories.filter((c) =>
    isCategoryScored(health, c.id),
  ).length;
  const gradeLabel = t(`grade.${health.grade}`);

  return (
    <div className="space-y-6">
      <KpiStrip>
        <KpiCell
          label={t("summary.score")}
          value={`${health.score} ${t("health.score")}`}
          subMeta={`${gradeLabel} · ${t("health.evaluatedShort", {
            evaluated: evaluatedCategories,
            total: ANALYSIS_CATEGORY_COUNT,
          })}`}
          valueClassName={getGradeColors(health.grade).text}
        />
        <KpiCell label={t("summary.appSize")} value={appSizeValue} subMeta={appSizeSub} />
        <KpiCell
          label={t("summary.appRam")}
          value={formatBytes(summary.staticByteSize || summary.totalBytes)}
          subMeta={t("summary.appRamDetail")}
        />
        <KpiCell
          label={t("summary.totalFields")}
          value={summary.totalFields}
          subMeta={t("summary.usedUnusedDetail", {
            used: summary.usedFields,
            unused: summary.unusedFields,
          })}
        />
        <KpiCell
          label={t("summary.wastedMemory")}
          value={formatBytes(wastedMemory)}
          subMeta={wastedMemorySub}
        />
        <KpiCell
          label={t("summary.appObjects")}
          value={summary.totalObjects}
          subMeta={t("summary.appObjectsDetail")}
        />
      </KpiStrip>

      <HealthCategoriesGrid health={health} />
      <HealthFindingsList health={health} />
    </div>
  );
}
